import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Edit, Trash2, Phone, MapPin, Star, Download, X, Upload, Eye, File } from 'lucide-react';
import api, { formatErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import CustomCheckbox from '@/components/Checkbox';
import Pagination from '@/components/Pagination';
import { Combobox } from '@/components/Combobox';
import Wizard from '@/components/Wizard';
import { InputMask } from '@/components/InputMask';
import { usePermissions } from '@/contexts/PermissionsContext';
import { ProtectedButton, ProtectedSection } from '@/components/rbac';

const Chauffeurs = () => {
  const { hasPermission } = usePermissions();
  const [chauffeurs, setChauffeurs] = useState([]);
  const [filteredChauffeurs, setFilteredChauffeurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatutContact, setFilterStatutContact] = useState('all');
  const [filterChauffeurCle, setFilterChauffeurCle] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Documents dialog state
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedChauffeurDocs, setSelectedChauffeurDocs] = useState(null);
  const [chauffeurDocuments, setChauffeurDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  
  // Upload permis dialog state
  const [uploadPermisDialogOpen, setUploadPermisDialogOpen] = useState(false);
  const [selectedChauffeurForUpload, setSelectedChauffeurForUpload] = useState(null);
  const [permisUploadData, setPermisUploadData] = useState({
    numero: '',
    date_emission: '',
    date_expiration: '',
    file: null
  });
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [bulkStatusData, setBulkStatusData] = useState({
    statut_verification: '',
    statut_plateforme: ''
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Filtres avancés
  const [filterOperateur, setFilterOperateur] = useState('all');
  const [filterPays, setFilterPays] = useState('all');
  const [filterTypeCamion, setFilterTypeCamion] = useState('all');
  const [filterSociete, setFilterSociete] = useState('all');
  const [filterProprietaire, setFilterProprietaire] = useState('all');
  const [filterStatutVerification, setFilterStatutVerification] = useState('all');
  const [filterStatutPlateforme, setFilterStatutPlateforme] = useState('all');
  const [filterServiceSpecifique, setFilterServiceSpecifique] = useState('');
  const [filterZoneCouverture, setFilterZoneCouverture] = useState('all');
  const [filterScoreMin, setFilterScoreMin] = useState('');
  const [filterIntraInterland, setFilterIntraInterland] = useState('all');
  
  // Liste des pays d'Afrique de l'Ouest
  const paysAfriqueOuest = [
    'Bénin',
    'Burkina Faso',
    'Cap-Vert',
    'Côte d\'Ivoire',
    'Gambie',
    'Ghana',
    'Guinée',
    'Guinée-Bissau',
    'Liberia',
    'Mali',
    'Mauritanie',
    'Niger',
    'Nigeria',
    'Sénégal',
    'Sierra Leone',
    'Togo'
  ];
  
  // Reference data for dropdowns
  const [users, setUsers] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [typesCamion, setTypesCamion] = useState([]);
  
  const [formData, setFormData] = useState({
    id: '',
    nom: '',
    telephone: '',
    telephone_2: '',
    pays: 'Côte d\'Ivoire',
    intra_inter_land: 'Intraland',
    statut_verification: 'Non vérifié',
    statut_plateforme: 'Nouveau',
    est_intermediaire: false,
    proprietaire_id: 'none',
    societe_transport_id: 'none',
    camions: [],
    pays_desservi: [],
    service_specifique: '',
    commentaire_operateur: '',
    lien_principal: 'proprietaire',
    // Nouveaux champs permis
    permis_ok: false,
    numero_permis: '',
    categorie_permis: '',
    date_emission_permis: '',
    date_expiration_permis: '',
    documents_permis: [],
    // Champs CNI
    cni_ok: false,
    numero_cni: '',
    date_emission_cni: '',
    date_expiration_cni: '',
  });
  
  // Wizard state
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  
  // Wizard steps configuration
  const wizardSteps = [
    {
      title: "Informations de base",
      description: "Nom, contact et localisation"
    },
    {
      title: "Rôles & Relations",
      description: "Opérateur, intermédiaire, partenaire"
    },
    {
      title: "Compétences",
      description: "Types camions, zone, service"
    },
    {
      title: "Permis de conduire",
      description: "Informations du permis"
    },
    {
      title: "Commentaires & Documents",
      description: "Notes et fichiers"
    }
  ];

  useEffect(() => {
    fetchChauffeurs();
    fetchReferenceData();
  }, []);

  useEffect(() => {
    let filtered = chauffeurs.filter((c) =>
      c.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.telephone.includes(searchTerm) ||
      c.pays.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter by statut_contact
    if (filterStatutContact !== 'all') {
      filtered = filtered.filter((c) => c.statut_contact === filterStatutContact);
    }

    // Filter by chauffeur clé
    if (filterChauffeurCle === 'cles') {
      filtered = filtered.filter((c) => c.is_clef === true);
    } else if (filterChauffeurCle === 'non-cles') {
      filtered = filtered.filter((c) => !c.is_clef);
    }

    // Filtres avancés
    if (filterPays !== 'all') {
      filtered = filtered.filter((c) => c.pays === filterPays);
    }
    
    if (filterTypeCamion !== 'all') {
      filtered = filtered.filter((c) => 
        c.camions && c.camions.some(camion => camion.type_camion_id === filterTypeCamion)
      );
    }
    
    if (filterSociete !== 'all') {
      filtered = filtered.filter((c) => c.societe_transport_id === filterSociete);
    }
    
    if (filterProprietaire !== 'all') {
      filtered = filtered.filter((c) => c.proprietaire_id === filterProprietaire);
    }
    
    if (filterStatutVerification !== 'all') {
      filtered = filtered.filter((c) => c.statut_verification === filterStatutVerification);
    }
    
    if (filterStatutPlateforme !== 'all') {
      filtered = filtered.filter((c) => c.statut_plateforme === filterStatutPlateforme);
    }
    
    if (filterServiceSpecifique !== '') {
      filtered = filtered.filter((c) => 
        c.service_specifique && c.service_specifique.toLowerCase().includes(filterServiceSpecifique.toLowerCase())
      );
    }
    
    if (filterZoneCouverture !== 'all') {
      filtered = filtered.filter((c) => 
        c.pays_desservi && c.pays_desservi.includes(filterZoneCouverture)
      );
    }
    
    if (filterScoreMin !== '') {
      const minScore = parseFloat(filterScoreMin);
      filtered = filtered.filter((c) => (c.score_fiabilite || 0) >= minScore);
    }
    
    if (filterIntraInterland !== 'all') {
      filtered = filtered.filter((c) => c.intra_inter_land === filterIntraInterland);
    }

    setFilteredChauffeurs(filtered);
  }, [searchTerm, chauffeurs, filterStatutContact, filterChauffeurCle, filterOperateur, filterPays, filterTypeCamion, filterSociete, filterProprietaire, filterStatutVerification, filterStatutPlateforme, filterServiceSpecifique, filterZoneCouverture, filterScoreMin, filterIntraInterland]);

  const fetchChauffeurs = async () => {
    try {
      const response = await api.get('/chauffeurs');
      setChauffeurs(response.data);
      setFilteredChauffeurs(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des chauffeurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [proprioRes, societesRes, typesCamionRes] = await Promise.all([
        api.get('/proprietaires'),
        api.get('/societes-transport'),
        api.get('/types-camion'),
      ]);
      console.log('Reference data loaded:', {
        proprietaires: proprioRes.data?.length,
        societes: societesRes.data?.length,
        typesCamion: typesCamionRes.data?.length
      });
      setProprietaires(proprioRes.data || []);
      setSocietes(societesRes.data || []);
      setTypesCamion(typesCamionRes.data || []);
      
      // Charger les users si l'utilisateur a la permission (optionnel)
      try {
        const usersRes = await api.get('/users');
        setUsers(usersRes.data || []);
      } catch (userError) {
        // Si l'utilisateur n'a pas la permission users.read, on continue sans
        if (userError.response?.status === 403) {
          console.log('ℹ️ Liste utilisateurs non disponible (permissions insuffisantes)');
          setUsers([]);
        } else {
          throw userError;
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données de référence:', error);
      toast.error('Erreur lors du chargement des données de référence');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const dataToSend = {
        nom: formData.nom,
        telephone: formData.telephone,
        telephone_2: formData.telephone_2 || null,
        pays: formData.pays,
        intra_inter_land: formData.intra_inter_land,
        est_intermediaire: formData.est_intermediaire,
        proprietaire_id: (formData.proprietaire_id && formData.proprietaire_id !== 'none') ? formData.proprietaire_id : null,
        societe_transport_id: (formData.societe_transport_id && formData.societe_transport_id !== 'none') ? formData.societe_transport_id : null,
        camions: formData.camions && formData.camions.length > 0 ? formData.camions : null,
        pays_desservi: formData.pays_desservi && formData.pays_desservi.length > 0 ? formData.pays_desservi : null,
        service_specifique: formData.service_specifique || null,
        commentaire_operateur: formData.commentaire_operateur || null,
        // Nouveaux champs permis
        permis_ok: formData.permis_ok,
        numero_permis: formData.numero_permis || null,
        categorie_permis: formData.categorie_permis || null,
        date_emission_permis: formData.date_emission_permis || null,
        date_expiration_permis: formData.date_expiration_permis || null,
      };

      let chauffeurId;
      if (editMode) {
        dataToSend.statut_verification = formData.statut_verification;
        dataToSend.statut_plateforme = formData.statut_plateforme;
        await api.put(`/chauffeurs/${formData.id}`, dataToSend);
        chauffeurId = formData.id;
        toast.success('Chauffeur modifié avec succès');
      } else {
        const response = await api.post('/chauffeurs', dataToSend);
        chauffeurId = response.data.id;
        toast.success('Chauffeur créé avec succès');
      }

      // Upload permis files if present (permis PDF, photo recto, photo verso)
      const filesToUpload = [
        { field: 'permis_file', file: formData.permis_file, label: 'Permis PDF' },
        { field: 'permis_photo', file: formData.permis_photo, label: 'Photo permis recto' },
        { field: 'permis_photo_verso', file: formData.permis_photo_verso, label: 'Photo permis verso' },
        { field: 'cni_file', file: formData.cni_file, label: 'CNI PDF' },
        { field: 'cni_photo_recto', file: formData.cni_photo_recto, label: 'Photo CNI recto' },
        { field: 'cni_photo_verso', file: formData.cni_photo_verso, label: 'Photo CNI verso' }
      ];
      
      const hasFiles = filesToUpload.some(item => item.file);
      
      if (hasFiles) {
        toast.info('Upload des documents en cours...');
        
        for (const item of filesToUpload) {
          if (item.file) {
            const docFormData = new FormData();
            docFormData.append('file', item.file);
            docFormData.append('type', item.field); // Type de fichier
            
            // Ajouter les infos selon le type de document
            if (item.field.includes('permis')) {
              docFormData.append('numero', formData.numero_permis || `PERM-${chauffeurId}`);
              docFormData.append('date_emission', formData.date_emission_permis || new Date().toISOString().split('T')[0]);
              docFormData.append('date_expiration', formData.date_expiration_permis || '2099-12-31');
            } else if (item.field.includes('cni')) {
              docFormData.append('numero', formData.numero_cni || `CNI-${chauffeurId}`);
              docFormData.append('date_emission', formData.date_emission_cni || new Date().toISOString().split('T')[0]);
              docFormData.append('date_expiration', formData.date_expiration_cni || '2099-12-31');
            }
            
            try {
              const endpoint = item.field.includes('permis') ? 'permis' : 'cni';
              await api.post(`/chauffeurs/${chauffeurId}/${endpoint}`, docFormData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              console.log(`✅ ${item.label} uploadé`);
            } catch (error) {
              console.error(`❌ Erreur upload ${item.label}:`, error);
              toast.error(`Erreur lors de l'upload: ${item.label}`);
            }
          }
        }
        
        toast.success('Documents uploadés avec succès');
      }

      setDialogOpen(false);
      resetForm();
      fetchChauffeurs();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'opération'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce chauffeur ?')) return;

    try {
      await api.delete(`/chauffeurs/${id}`);
      toast.success('Chauffeur supprimé');
      fetchChauffeurs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleViewDocuments = async (chauffeur) => {
    setSelectedChauffeurDocs(chauffeur);
    setLoadingDocuments(true);
    setDocumentsDialogOpen(true);
    setPreviewDocument(null);
    
    try {
      // Récupérer les données complètes du chauffeur avec ses documents
      const response = await api.get(`/chauffeurs/${chauffeur.id}`);
      const fullChauffeur = response.data;
      
      // Construire la liste des documents à partir des champs documents_permis et autres
      const docs = [];
      
      // Documents permis
      if (fullChauffeur.documents_permis && fullChauffeur.documents_permis.length > 0) {
        fullChauffeur.documents_permis.forEach((permis, index) => {
          docs.push({
            id: `permis-${index}`,
            type: 'Permis de conduire',
            numero: permis.numero,
            date_emission: permis.date_emission,
            date_expiration: permis.date_expiration,
            statut: permis.statut,
            fichier_url: permis.fichier_url,
            filename: permis.fichier_url?.split('/').pop(),
            previewUrl: `${process.env.REACT_APP_BACKEND_URL}${permis.fichier_url}`
          });
        });
      }
      
      // Documents CNI (si disponibles dans le futur)
      if (fullChauffeur.documents_cni && fullChauffeur.documents_cni.length > 0) {
        fullChauffeur.documents_cni.forEach((cni, index) => {
          docs.push({
            id: `cni-${index}`,
            type: 'CNI',
            numero: cni.numero,
            date_emission: cni.date_emission,
            date_expiration: cni.date_expiration,
            statut: cni.statut,
            fichier_url: cni.fichier_url,
            filename: cni.fichier_url?.split('/').pop(),
            previewUrl: `${process.env.REACT_APP_BACKEND_URL}${cni.fichier_url}`
          });
        });
      }
      
      setChauffeurDocuments(docs);
      
      // Auto-sélectionner le premier document pour la prévisualisation
      if (docs.length > 0) {
        setPreviewDocument(docs[0]);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des documents');
      setChauffeurDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };
  
  const handleDownloadDocument = async (doc) => {
    try {
      // Utiliser l'URL du fichier directement depuis le document
      const fileUrl = doc.fichier_url || doc.previewUrl;
      const filename = doc.filename || fileUrl?.split('/').pop() || 'document';
      
      const response = await api.get(fileUrl.startsWith('http') ? fileUrl : `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document téléchargé');
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openUploadPermisDialog = (chauffeur) => {
    setSelectedChauffeurForUpload(chauffeur);
    setPermisUploadData({
      numero: '',
      date_emission: '',
      date_expiration: '',
      file: null
    });
    setUploadPermisDialogOpen(true);
  };

  const handlePermisUpload = async () => {
    try {
      if (!permisUploadData.numero || !permisUploadData.date_emission || !permisUploadData.date_expiration || !permisUploadData.file) {
        toast.error('Veuillez remplir tous les champs du permis');
        return;
      }
      
      const formDataToSend = new FormData();
      formDataToSend.append('numero', permisUploadData.numero);
      formDataToSend.append('date_emission', permisUploadData.date_emission);
      formDataToSend.append('date_expiration', permisUploadData.date_expiration);
      formDataToSend.append('file', permisUploadData.file);
      
      await api.post(`/chauffeurs/${selectedChauffeurForUpload.id}/permis`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      toast.success('Permis ajouté avec succès');
      
      // Close dialog and reset
      setUploadPermisDialogOpen(false);
      setPermisUploadData({
        numero: '',
        date_emission: '',
        date_expiration: '',
        file: null
      });
      
      // Refresh chauffeurs list
      fetchChauffeurs();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'upload du permis'));
    }
  };


  // Bulk selection handlers
  const handleSelectAll = () => {
    const currentPageIds = paginatedChauffeurs.map(c => c.id);
    setSelectedIds(currentPageIds);
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleBulkUpdateStatus = () => {
    setUpdateStatusDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await api.post('/chauffeurs/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} chauffeur(s) supprimé(s)`);
      setSelectedIds([]);
      setDeleteConfirmOpen(false);
      fetchChauffeurs();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const confirmBulkUpdateStatus = async () => {
    try {
      const payload = { ids: selectedIds };
      if (bulkStatusData.statut_verification) {
        payload.statut_verification = bulkStatusData.statut_verification;
      }
      if (bulkStatusData.statut_plateforme) {
        payload.statut_plateforme = bulkStatusData.statut_plateforme;
      }

      if (!payload.statut_verification && !payload.statut_plateforme) {
        toast.error('Veuillez sélectionner au moins un statut à mettre à jour');
        return;
      }

      await api.post('/chauffeurs/bulk-update-status', payload);
      toast.success(`${selectedIds.length} chauffeur(s) mis à jour`);
      setSelectedIds([]);
      setUpdateStatusDialogOpen(false);
      setBulkStatusData({ statut_verification: '', statut_plateforme: '' });
      fetchChauffeurs();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la mise à jour'));
    }
  };

  // Pagination logic with useMemo
  const totalPages = useMemo(() => Math.ceil(filteredChauffeurs.length / pageSize), [filteredChauffeurs.length, pageSize]);
  const paginatedChauffeurs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredChauffeurs.slice(startIndex, endIndex);
  }, [filteredChauffeurs, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedIds([]); // Désélectionner lors du changement de page
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Retourner à la première page
    setSelectedIds([]);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [searchTerm, filterStatutContact, filterChauffeurCle, filterOperateur, filterPays, 
      filterTypeCamion, filterSociete, filterProprietaire, filterStatutVerification,
      filterStatutPlateforme, filterServiceSpecifique, filterZoneCouverture, 
      filterScoreMin, filterIntraInterland]);

  // Reset telephone when country changes (to apply new country code)
  useEffect(() => {
    if (formData.pays && !editMode) {
      // Only reset if phone is empty or still has old country code
      const phoneDigits = formData.telephone.replace(/\D/g, '');
      if (phoneDigits.length === 0 || phoneDigits.length < 3) {
        setFormData(prev => ({ ...prev, telephone: '' }));
      }
    }
  }, [formData.pays]);

  const handleToggleChauffeurCle = async (id, currentValue) => {
    try {
      await api.put(`/chauffeurs/${id}/cle`, { is_clef: !currentValue });
      toast.success(!currentValue ? 'Chauffeur marqué comme clé' : 'Chauffeur retiré des clés');
      fetchChauffeurs();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/export/chauffeurs?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        // Télécharger CSV
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const filename = `chauffeurs_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Toast persistant avec plus d'infos
        toast.success(`✓ Export CSV réussi - ${filename}`, {
          duration: 5000,
          description: 'Le fichier a été téléchargé dans votre dossier de téléchargements'
        });
      } else {
        // Télécharger JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const filename = `chauffeurs_export_${new Date().toISOString().split('T')[0]}.json`;
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Toast persistant avec plus d'infos
        toast.success(`✓ Export JSON réussi - ${filename}`, {
          duration: 5000,
          description: 'Le fichier a été téléchargé dans votre dossier de téléchargements'
        });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export', {
        description: error.response?.data?.detail || 'Veuillez réessayer'
      });
    }
  };

  const openEditDialog = (chauffeur) => {
    setFormData({
      ...chauffeur,
      proprietaire_id: chauffeur.proprietaire_id || 'none',
      societe_transport_id: chauffeur.societe_transport_id || 'none',
      camions: chauffeur.camions || [],
      pays_desservi: chauffeur.pays_desservi || [],
      service_specifique: chauffeur.service_specifique || '',
      commentaire_operateur: chauffeur.commentaire_operateur || '',
      telephone_2: chauffeur.telephone_2 || '',
      // Champs permis
      permis_ok: chauffeur.permis_ok || false,
      numero_permis: chauffeur.numero_permis || '',
      categorie_permis: chauffeur.categorie_permis || '',
      date_emission_permis: chauffeur.date_emission_permis || '',
      date_expiration_permis: chauffeur.date_expiration_permis || '',
      documents_permis: chauffeur.documents_permis || [],
      // Champs CNI
      cni_ok: chauffeur.cni_ok || false,
      numero_cni: chauffeur.numero_cni || '',
      date_emission_cni: chauffeur.date_emission_cni || '',
      date_expiration_cni: chauffeur.date_expiration_cni || '',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const ajouterCamion = () => {
    setFormData({
      ...formData,
      camions: [...(formData.camions || []), { type_camion_id: '', capacite_tonnes: 10 }]
    });
  };

  const supprimerCamion = (index) => {
    const nouveauxCamions = formData.camions.filter((_, i) => i !== index);
    setFormData({ ...formData, camions: nouveauxCamions });
  };

  const modifierCamion = (index, field, value) => {
    const nouveauxCamions = [...formData.camions];
    nouveauxCamions[index][field] = field === 'capacite_tonnes' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, camions: nouveauxCamions });
  };

  const togglePaysDesservi = (pays) => {
    const paysActuels = formData.pays_desservi || [];
    if (paysActuels.includes(pays)) {
      setFormData({ ...formData, pays_desservi: paysActuels.filter(p => p !== pays) });
    } else {
      setFormData({ ...formData, pays_desservi: [...paysActuels, pays] });
    }
  };

  const retirerPaysDesservi = (pays) => {
    const paysActuels = formData.pays_desservi || [];
    setFormData({ ...formData, pays_desservi: paysActuels.filter(p => p !== pays) });
  };

  const resetForm = () => {
    setFormData({
      id: '',
      nom: '',
      telephone: '',
      telephone_2: '',
      pays: 'Côte d\'Ivoire',
      intra_inter_land: 'Intraland',
      statut_verification: 'Non vérifié',
      statut_plateforme: 'Nouveau',
      operateur_id: 'none',
      est_intermediaire: false,
      proprietaire_id: 'none',
      societe_transport_id: 'none',
      camions: [],
      pays_desservi: [],
      service_specifique: '',
      commentaire_operateur: '',
      lien_principal: 'proprietaire',
      permis_ok: false,
      numero_permis: '',
      categorie_permis: '',
      date_emission_permis: '',
      date_expiration_permis: '',
      documents_permis: [],
      cni_ok: false,
      numero_cni: '',
      date_emission_cni: '',
      date_expiration_cni: '',
    });
    setEditMode(false);
    setCurrentWizardStep(0);
  };
  
  // Wizard navigation handlers
  const handleWizardNext = () => {
    if (currentWizardStep < wizardSteps.length - 1) {
      setCurrentWizardStep(currentWizardStep + 1);
    }
  };

  const handleWizardPrev = () => {
    if (currentWizardStep > 0) {
      setCurrentWizardStep(currentWizardStep - 1);
    }
  };

  const canProceedToNextStep = () => {
    switch(currentWizardStep) {
      case 0: // Step 1: Basic info
        return formData.nom && formData.telephone && formData.pays;
      case 1: // Step 2: Roles & Relations (all optional, can proceed)
        return true;
      case 2: // Step 3: Compétences (optional, can proceed)
        return true;
      case 3: // Step 4: Permis (optional, can proceed)
        return true;
      case 4: // Step 5: Comments & Documents (optional, can proceed)
        return true;
      default:
        return true;
    }
  };

  // Render wizard step content
  const renderWizardStepContent = () => {
    switch(currentWizardStep) {
      case 0: // Step 1: Informations de base
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom complet *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: KOUAME Koffi"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telephone">Téléphone principal *</Label>
              <InputMask
                mask="phone"
                country={formData.pays || "Côte d'Ivoire"}
                id="telephone"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500">
                Format automatique selon le pays sélectionné
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telephone_2">Téléphone secondaire (Optionnel)</Label>
              <InputMask
                mask="phone"
                country={formData.pays || "Côte d'Ivoire"}
                id="telephone_2"
                value={formData.telephone_2 || ''}
                onChange={(e) => setFormData({ ...formData, telephone_2: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                Numéro de contact alternatif
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pays">Pays *</Label>
              <Combobox
                options={paysAfriqueOuest.map(pays => ({ value: pays, label: pays }))}
                value={formData.pays}
                onValueChange={(value) => setFormData({ ...formData, pays: value })}
                placeholder="Sélectionnez un pays"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun pays trouvé"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="intra_inter_land">Type de transport</Label>
              <Select
                value={formData.intra_inter_land}
                onValueChange={(value) => setFormData({ ...formData, intra_inter_land: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Intraland">Intraland</SelectItem>
                  <SelectItem value="Interland">Interland</SelectItem>
                  <SelectItem value="Les deux (Intra-Inter)">Les deux (Intra-Inter)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
        
      case 1: // Step 2: Rôles & Relations
        return (
          <div className="space-y-6">
            {/* Statut Intermédiaire */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Statut Intermédiaire</h3>
              <div className="flex items-center space-x-2">
                <Switch
                  id="est_intermediaire"
                  checked={formData.est_intermediaire}
                  onCheckedChange={(checked) => setFormData({ ...formData, est_intermediaire: checked })}
                />
                <Label htmlFor="est_intermediaire">Ce chauffeur est un intermédiaire</Label>
              </div>
            </div>
            
            {/* Partenaire / Société */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Partenaire / Société de transport (Optionnel)</h3>
              <p className="text-xs text-gray-500 mb-3">
                Pour les chauffeurs indépendants propriétaires de leur propre camion, sélectionnez "Aucun"
              </p>
              <div className="space-y-3">
                <RadioGroup
                  value={formData.lien_principal}
                  onValueChange={(value) => setFormData({ ...formData, lien_principal: value })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="proprietaire" id="lien_proprietaire" />
                    <Label htmlFor="lien_proprietaire">Partenaire</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="societe" id="lien_societe" />
                    <Label htmlFor="lien_societe">Société</Label>
                  </div>
                </RadioGroup>
                
                {formData.lien_principal === 'proprietaire' && (
                  <div>
                    <Combobox
                      options={[
                        { value: "none", label: "Aucun" },
                        ...(proprietaires || []).map(p => ({ 
                          value: p.id, 
                          label: `${p.nom} - ${p.pays}` 
                        }))
                      ]}
                      value={formData.proprietaire_id}
                      onValueChange={(value) => setFormData({ ...formData, proprietaire_id: value })}
                      placeholder="Sélectionner un partenaire"
                      searchPlaceholder="Rechercher..."
                      emptyText={proprietaires.length === 0 ? "Chargement..." : "Aucun partenaire trouvé"}
                    />
                    {proprietaires.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">Chargement des partenaires...</p>
                    )}
                  </div>
                )}
                
                {formData.lien_principal === 'societe' && (
                  <div>
                    <Combobox
                      options={[
                        { value: "none", label: "Aucune" },
                        ...(societes || []).map(s => ({ 
                          value: s.id, 
                          label: `${s.nom} - ${s.pays}` 
                        }))
                      ]}
                      value={formData.societe_transport_id}
                      onValueChange={(value) => setFormData({ ...formData, societe_transport_id: value })}
                      placeholder="Sélectionner une société"
                      searchPlaceholder="Rechercher..."
                      emptyText={societes.length === 0 ? "Chargement..." : "Aucune société trouvée"}
                    />
                    {societes.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">Chargement des sociétés...</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        
      case 2: // Step 3: Compétences
        return (
          <div className="space-y-6">
            {/* Types de camions */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Types de camions et capacités</h3>
              <div className="space-y-3">
                <Combobox
                  options={(typesCamion || []).map(type => ({
                    value: type.id,
                    label: type.type_camion || type.libelle
                  }))}
                  value=""
                  onValueChange={(value) => {
                    const selected = (typesCamion || []).find(t => t.id === value);
                    if (selected) {
                      // Ne plus bloquer les doublons - on peut avoir le même type avec capacités différentes
                      setFormData({
                        ...formData,
                        camions: [...formData.camions, { 
                          type_camion_id: value, 
                          capacite_tonnes: 0,  // À définir par l'utilisateur
                          nombre_camions: 1    // Par défaut 1 camion
                        }]
                      });
                    }
                  }}
                  placeholder="Ajouter un type de camion"
                  searchPlaceholder="Rechercher..."
                  emptyText={(typesCamion || []).length === 0 ? "Chargement..." : "Aucun type trouvé"}
                />
                {(typesCamion || []).length === 0 && (
                  <p className="text-xs text-gray-500">Chargement des types de camion...</p>
                )}
                
                {formData.camions.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600 font-medium bg-gray-50 px-3 py-2 rounded-md">
                      {formData.camions.length} type{formData.camions.length > 1 ? 's' : ''} de camion{formData.camions.length > 1 ? 's' : ''} ajouté{formData.camions.length > 1 ? 's' : ''} 
                      {' - '}Total: {formData.camions.reduce((sum, c) => sum + (c.nombre_camions || 1), 0)} camion{formData.camions.reduce((sum, c) => sum + (c.nombre_camions || 1), 0) > 1 ? 's' : ''}
                    </div>
                    {formData.camions.map((camion, index) => {
                      const type = (typesCamion || []).find(t => t.id === camion.type_camion_id);
                      return (
                        <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-800">{type?.type_camion || type?.libelle}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  camions: formData.camions.filter((_, i) => i !== index)
                                });
                              }}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Capacité (T)</Label>
                              <Input
                                type="number"
                                step="0.5"
                                value={camion.capacite_tonnes}
                                onChange={(e) => {
                                  const newCamions = [...formData.camions];
                                  newCamions[index].capacite_tonnes = parseFloat(e.target.value) || 0;
                                  setFormData({ ...formData, camions: newCamions });
                                }}
                                className="h-9"
                                placeholder="Ex: 10"
                              />
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-600">Nombre de camions</Label>
                              <Input
                                type="number"
                                min="1"
                                value={camion.nombre_camions || 1}
                                onChange={(e) => {
                                  const newCamions = [...formData.camions];
                                  newCamions[index].nombre_camions = parseInt(e.target.value) || 1;
                                  setFormData({ ...formData, camions: newCamions });
                                }}
                                className="h-9"
                                placeholder="Ex: 5"
                              />
                            </div>
                          </div>
                          
                          <div className="mt-2 text-xs text-gray-600">
                            = <span className="font-semibold text-emerald-700">{camion.nombre_camions || 1} × {type?.type_camion || 'Type'} de {camion.capacite_tonnes || 0} T</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            
            {/* Zone de couverture */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Zone de couverture</h3>
              <div className="space-y-3">
                <Combobox
                  options={paysAfriqueOuest.map(pays => ({ value: pays, label: pays }))}
                  value=""
                  onValueChange={(value) => {
                    if (!formData.pays_desservi.includes(value)) {
                      setFormData({
                        ...formData,
                        pays_desservi: [...formData.pays_desservi, value]
                      });
                    }
                  }}
                  placeholder="Ajouter un pays"
                  searchPlaceholder="Rechercher..."
                  emptyText="Aucun pays trouvé"
                />
                
                {formData.pays_desservi.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.pays_desservi.map((pays, index) => (
                      <Badge key={index} variant="secondary" className="gap-1">
                        {pays}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              pays_desservi: formData.pays_desservi.filter((_, i) => i !== index)
                            });
                          }}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Service spécifique */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Service spécifique</h3>
              <Input
                value={formData.service_specifique}
                onChange={(e) => setFormData({ ...formData, service_specifique: e.target.value })}
                placeholder="Ex: Transport de marchandises dangereuses..."
              />
            </div>
          </div>
        );
        
      case 3: // Step 4: Permis de conduire
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-gray-700 mb-3">📄 Permis de conduire</h3>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="permis_ok"
                checked={formData.permis_ok}
                onCheckedChange={(checked) => setFormData({ ...formData, permis_ok: checked })}
              />
              <Label htmlFor="permis_ok">Permis OK</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numero_permis">Numéro de permis</Label>
                <Input
                  id="numero_permis"
                  value={formData.numero_permis}
                  onChange={(e) => setFormData({ ...formData, numero_permis: e.target.value })}
                  placeholder="Ex: PCI123456789"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="categorie_permis">Catégorie</Label>
                <Select
                  value={formData.categorie_permis || "none"}
                  onValueChange={(value) => setFormData({ ...formData, categorie_permis: value === "none" ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Aucune --</SelectItem>
                    <SelectItem value="B">B (Véhicules légers)</SelectItem>
                    <SelectItem value="C">C (Poids lourds)</SelectItem>
                    <SelectItem value="D">D (Transport de personnes)</SelectItem>
                    <SelectItem value="E">E (Remorques)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="date_emission_permis">Date d'émission</Label>
                <Input
                  id="date_emission_permis"
                  type="date"
                  value={formData.date_emission_permis}
                  onChange={(e) => setFormData({ ...formData, date_emission_permis: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="date_expiration_permis">Date d'expiration</Label>
                <Input
                  id="date_expiration_permis"
                  type="date"
                  value={formData.date_expiration_permis}
                  onChange={(e) => setFormData({ ...formData, date_expiration_permis: e.target.value })}
                />
              </div>
            </div>
            
            {/* Séparateur */}
            <div className="border-t pt-4 mt-4"></div>
            
            {/* Section CNI */}
            <h3 className="font-semibold text-sm text-gray-700 mb-3">🪪 Carte Nationale d'Identité (CNI)</h3>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cni_ok"
                checked={formData.cni_ok}
                onCheckedChange={(checked) => setFormData({ ...formData, cni_ok: checked })}
              />
              <Label htmlFor="cni_ok">CNI OK</Label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numero_cni">Numéro de CNI</Label>
                <Input
                  id="numero_cni"
                  value={formData.numero_cni}
                  onChange={(e) => setFormData({ ...formData, numero_cni: e.target.value })}
                  placeholder="Ex: CI0123456789"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="date_emission_cni">Date d'émission</Label>
                <Input
                  id="date_emission_cni"
                  type="date"
                  value={formData.date_emission_cni}
                  onChange={(e) => setFormData({ ...formData, date_emission_cni: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="date_expiration_cni">Date d'expiration</Label>
                <Input
                  id="date_expiration_cni"
                  type="date"
                  value={formData.date_expiration_cni}
                  onChange={(e) => setFormData({ ...formData, date_expiration_cni: e.target.value })}
                />
              </div>
            </div>
          </div>
        );
        
      case 4: // Step 5: Commentaires & Documents
        return (
          <div className="space-y-6">
            {/* Commentaire */}
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">💬 Commentaire opérateur</h3>
              <Textarea
                value={formData.commentaire_operateur}
                onChange={(e) => setFormData({ ...formData, commentaire_operateur: e.target.value })}
                placeholder="Notes ou observations..."
                rows={4}
              />
            </div>
            
            {/* Statuts */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statut de vérification</Label>
                <Select
                  value={formData.statut_verification}
                  onValueChange={(value) => setFormData({ ...formData, statut_verification: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Non vérifié">Non vérifié</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="Vérifié">Vérifié</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Statut plateforme</Label>
                <Select
                  value={formData.statut_plateforme}
                  onValueChange={(value) => setFormData({ ...formData, statut_plateforme: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nouveau">Nouveau</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="Actif">Actif</SelectItem>
                    <SelectItem value="Suspendu">Suspendu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Documents upload */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">📁 Documents du permis de conduire</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="permis_file">📄 Permis de conduire (PDF, PNG, JPG)</Label>
                  <Input
                    id="permis_file"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setFormData({ ...formData, permis_file: e.target.files[0] })}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Taille max: 5 MB - Format: PDF, PNG, JPG
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="permis_photo">📸 Photo du permis (recto)</Label>
                  <Input
                    id="permis_photo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, permis_photo: e.target.files[0] })}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Photo claire du recto du permis
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="permis_photo_verso">📸 Photo du permis (verso)</Label>
                  <Input
                    id="permis_photo_verso"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, permis_photo_verso: e.target.files[0] })}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Photo claire du verso du permis
                  </p>
                </div>
              </div>
            </div>
            
            {/* Documents CNI */}
            <div className="bg-green-50 p-4 rounded-lg space-y-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">🪪 Documents de la CNI</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cni_file">📄 CNI (PDF, PNG, JPG)</Label>
                  <Input
                    id="cni_file"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setFormData({ ...formData, cni_file: e.target.files[0] })}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Taille max: 5 MB - Format: PDF, PNG, JPG
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cni_photo_recto">📸 Photo CNI (recto)</Label>
                  <Input
                    id="cni_photo_recto"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, cni_photo_recto: e.target.files[0] })}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Photo claire du recto de la CNI
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cni_photo_verso">📸 Photo CNI (verso)</Label>
                  <Input
                    id="cni_photo_verso"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, cni_photo_verso: e.target.files[0] })}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    Photo claire du verso de la CNI
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const getStatusBadge = (statut, type) => {
    const statusClasses = {
      'Vérifié': 'status-badge status-verifie',
      'Non vérifié': 'status-badge status-manquant',
      'En cours': 'status-badge bg-blue-100 text-blue-700',
      'Actif': 'status-badge status-actif',
      'Suspendu': 'status-badge status-suspendu',
      'Nouveau': 'status-badge bg-purple-100 text-purple-700',
    };
    return <span className={statusClasses[statut] || 'status-badge'}>{statut}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="chauffeurs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Chauffeurs</h1>
          <p className="text-gray-600 mt-2">Gestion des chauffeurs</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Boutons export visibles uniquement avec permission "export" */}
          <ProtectedButton
            resource="chauffeurs"
            action="export"
            onClick={() => handleExport('csv')}
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </ProtectedButton>
          
          <ProtectedButton
            resource="chauffeurs"
            action="export"
            onClick={() => handleExport('json')}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
            data-testid="export-json-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter JSON
          </ProtectedButton>
          
          {/* Bouton ajouter visible uniquement avec permission "create" */}
          <ProtectedButton
            resource="chauffeurs"
            action="create"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-chauffeur-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Ajouter un chauffeur
          </ProtectedButton>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative col-span-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, téléphone, pays..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
                data-testid="search-chauffeur-input"
              />
            </div>

            {/* Filter Statut Contact */}
            <div className="space-y-1">
              <Label className="text-sm font-medium text-gray-700">Statut Contact</Label>
              <Select value={filterStatutContact} onValueChange={setFilterStatutContact}>
                <SelectTrigger data-testid="filter-statut-contact">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="Joignable">Joignable</SelectItem>
                  <SelectItem value="À recontacter">À recontacter</SelectItem>
                  <SelectItem value="Injoignable">Injoignable</SelectItem>
                  <SelectItem value="Numéro invalide">Numéro invalide</SelectItem>
                  <SelectItem value="Inconnu">Inconnu</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter Chauffeur Clé */}
            <div className="space-y-1">
              <Label className="text-sm font-medium text-gray-700">Type Chauffeur</Label>
              <Select value={filterChauffeurCle} onValueChange={setFilterChauffeurCle}>
                <SelectTrigger data-testid="filter-chauffeur-cle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="cles">⭐ Chauffeurs clés uniquement</SelectItem>
                  <SelectItem value="non-cles">Chauffeurs standards</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Bouton Filtres avancés */}
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="w-full"
            >
              {showAdvancedFilters ? '▲ Masquer les filtres avancés' : '▼ Afficher les filtres avancés'}
            </Button>
          </div>
          
          {/* Filtres avancés */}
          {showAdvancedFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Filtres avancés</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Filtre Opérateur */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Opérateur</Label>
                  <Select value={filterOperateur} onValueChange={setFilterOperateur}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les opérateurs</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Pays */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Pays</Label>
                  <Select value={filterPays} onValueChange={setFilterPays}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les pays</SelectItem>
                      {paysAfriqueOuest.map((pays) => (
                        <SelectItem key={pays} value={pays}>
                          {pays}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Type de camion */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Type de camion</Label>
                  <Select value={filterTypeCamion} onValueChange={setFilterTypeCamion}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {(typesCamion || []).map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.type_camion}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Société */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Société de transport</Label>
                  <Select value={filterSociete} onValueChange={setFilterSociete}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les sociétés</SelectItem>
                      {societes.map((societe) => (
                        <SelectItem key={societe.id} value={societe.id}>
                          {societe.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Partenaire */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Partenaire</Label>
                  <Select value={filterProprietaire} onValueChange={setFilterProprietaire}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les propriétaires</SelectItem>
                      {proprietaires.map((proprio) => (
                        <SelectItem key={proprio.id} value={proprio.id}>
                          {proprio.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Inter/Intraland */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Inter/Intraland</Label>
                  <Select value={filterIntraInterland} onValueChange={setFilterIntraInterland}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="Interland">Interland</SelectItem>
                      <SelectItem value="Intraland">Intraland</SelectItem>
                      <SelectItem value="Les deux">Les deux</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Statut Vérification */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Statut vérification</Label>
                  <Select value={filterStatutVerification} onValueChange={setFilterStatutVerification}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="Non vérifié">Non vérifié</SelectItem>
                      <SelectItem value="En cours">En cours</SelectItem>
                      <SelectItem value="Vérifié">Vérifié</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Statut Plateforme */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Statut plateforme</Label>
                  <Select value={filterStatutPlateforme} onValueChange={setFilterStatutPlateforme}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="Nouveau">Nouveau</SelectItem>
                      <SelectItem value="En cours">En cours</SelectItem>
                      <SelectItem value="Actif">Actif</SelectItem>
                      <SelectItem value="Suspendu">Suspendu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Zone de couverture */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Zone de couverture</Label>
                  <Select value={filterZoneCouverture} onValueChange={setFilterZoneCouverture}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les zones</SelectItem>
                      {paysAfriqueOuest.map((pays) => (
                        <SelectItem key={pays} value={pays}>
                          {pays}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filtre Service spécifique */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Service spécifique</Label>
                  <Input
                    type="text"
                    placeholder="Ex: Transport frigorifique"
                    value={filterServiceSpecifique}
                    onChange={(e) => setFilterServiceSpecifique(e.target.value)}
                  />
                </div>

                {/* Filtre Score minimum */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-700">Score minimum</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Ex: 50"
                    value={filterScoreMin}
                    onChange={(e) => setFilterScoreMin(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Liste des chauffeurs ({filteredChauffeurs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions Toolbar */}
          <BulkActionsToolbar
            selectedCount={selectedIds.length}
            totalCount={paginatedChauffeurs.length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onBulkDelete={handleBulkDelete}
            onBulkAction={handleBulkUpdateStatus}
            bulkActionLabel="Mettre à jour statuts"
            bulkActionIcon={<Edit className="h-4 w-4" />}
            entityName="chauffeur"
          />

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                    <CustomCheckbox
                      checked={selectedIds.length === paginatedChauffeurs.length && paginatedChauffeurs.length > 0}
                      onChange={(checked) => checked ? handleSelectAll() : handleDeselectAll()}
                    />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Matricule</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Nom</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Téléphone</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Téléphone 2</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Pays</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Camions</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Zone</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Score</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Vérif</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Plateforme</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Clé</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedChauffeurs.map((chauffeur) => (
                  <tr key={chauffeur.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <CustomCheckbox
                        checked={selectedIds.includes(chauffeur.id)}
                        onChange={() => handleToggleSelect(chauffeur.id)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-mono text-xs text-gray-600">{chauffeur.matricule || '-'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{chauffeur.nom}</span>
                        {chauffeur.is_clef && (
                          <Badge variant="default" className="bg-amber-100 text-amber-700 border-amber-300">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Clé
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="h-4 w-4 text-gray-400" />
                        {chauffeur.telephone}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {chauffeur.telephone_2 ? (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {chauffeur.telephone_2}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm italic">Non renseigné</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {chauffeur.pays}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{chauffeur.intra_inter_land}</td>
                    <td className="py-3 px-4">
                      {chauffeur.camions && chauffeur.camions.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {chauffeur.camions.slice(0, 3).map((camion, idx) => {
                            const typeCamion = (typesCamion || []).find(t => t.id === camion.type_camion_id);
                            const nombre = camion.nombre_camions || 1;
                            return (
                              <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium whitespace-nowrap">
                                {nombre > 1 && `${nombre} × `}{typeCamion ? typeCamion.type_camion : 'N/A'} {camion.capacite_tonnes}T
                              </span>
                            );
                          })}
                          {chauffeur.camions.length > 3 && (
                            <span 
                              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-medium cursor-help"
                              title={chauffeur.camions.slice(3).map(c => {
                                const type = (typesCamion || []).find(t => t.id === c.type_camion_id);
                                const nb = c.nombre_camions || 1;
                                return `${nb > 1 ? nb + ' × ' : ''}${type?.type_camion || 'N/A'} ${c.capacite_tonnes}T`;
                              }).join('\n')}
                            >
                              +{chauffeur.camions.length - 3} autre{chauffeur.camions.length - 3 > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {chauffeur.pays_desservi && chauffeur.pays_desservi.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {chauffeur.pays_desservi.slice(0, 2).map((pays, idx) => (
                            <span key={idx} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                              {pays}
                            </span>
                          ))}
                          {chauffeur.pays_desservi.length > 2 && (
                            <span className="text-xs text-gray-500">+{chauffeur.pays_desservi.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600">
                        {chauffeur.service_specifique || '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Badge 
                          variant="outline"
                          className={
                            (chauffeur.score_fiabilite || 0) >= 75 ? 'bg-green-50 text-green-700 border-green-300' :
                            (chauffeur.score_fiabilite || 0) >= 50 ? 'bg-yellow-50 text-yellow-700 border-yellow-300' :
                            'bg-red-50 text-red-700 border-red-300'
                          }
                        >
                          {(chauffeur.score_fiabilite || 0).toFixed(0)}/100
                        </Badge>
                      </div>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(chauffeur.statut_verification)}</td>
                    <td className="py-3 px-4">{getStatusBadge(chauffeur.statut_plateforme)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center">
                        <Switch
                          checked={chauffeur.is_clef || false}
                          onCheckedChange={() => handleToggleChauffeurCle(chauffeur.id, chauffeur.is_clef)}
                          data-testid={`toggle-cle-${chauffeur.id}`}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(chauffeur)}
                          data-testid={`edit-chauffeur-${chauffeur.id}`}
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewDocuments(chauffeur)}
                          data-testid={`view-docs-${chauffeur.id}`}
                          title="Voir les documents"
                        >
                          <Eye className="h-4 w-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openUploadPermisDialog(chauffeur)}
                          data-testid={`upload-chauffeur-${chauffeur.id}`}
                          title="Upload documents permis"
                        >
                          <Upload className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(chauffeur.id)}
                          data-testid={`delete-chauffeur-${chauffeur.id}`}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredChauffeurs.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Aucun chauffeur trouvé
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredChauffeurs.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredChauffeurs.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedIds.length} chauffeur(s) ?
              Cette action est irréversible et supprimera également tous les documents associés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Status Dialog */}
      <Dialog open={updateStatusDialogOpen} onOpenChange={setUpdateStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mettre à jour les statuts</DialogTitle>
            <DialogDescription>
              Mettez à jour les statuts pour {selectedIds.length} chauffeur(s) sélectionné(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Statut de vérification</Label>
              <Select 
                value={bulkStatusData.statut_verification} 
                onValueChange={(value) => setBulkStatusData(prev => ({ ...prev, statut_verification: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un statut (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Ne pas modifier --</SelectItem>
                  <SelectItem value="Non vérifié">Non vérifié</SelectItem>
                  <SelectItem value="En cours">En cours</SelectItem>
                  <SelectItem value="Vérifié">Vérifié</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statut plateforme</Label>
              <Select 
                value={bulkStatusData.statut_plateforme} 
                onValueChange={(value) => setBulkStatusData(prev => ({ ...prev, statut_plateforme: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un statut (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Ne pas modifier --</SelectItem>
                  <SelectItem value="Non enregistré">Non enregistré</SelectItem>
                  <SelectItem value="Nouveau">Nouveau</SelectItem>
                  <SelectItem value="En cours">En cours</SelectItem>
                  <SelectItem value="Actif">Actif</SelectItem>
                  <SelectItem value="Suspendu">Suspendu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateStatusDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={confirmBulkUpdateStatus}>
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog with Wizard */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setCurrentWizardStep(0); // Reset wizard when closing
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="chauffeur-dialog">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Modifier le chauffeur' : 'Ajouter un chauffeur'}</DialogTitle>
              <DialogDescription>
                Remplissez les informations du chauffeur en suivant les étapes
              </DialogDescription>
            </DialogHeader>
            
            <Wizard
              steps={wizardSteps}
              currentStep={currentWizardStep}
              onNext={handleWizardNext}
              onPrev={handleWizardPrev}
              onSubmit={handleSubmit}
              canProceed={canProceedToNextStep()}
            >
              {renderWizardStepContent()}
            </Wizard>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog with Preview */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              📁 Documents de {selectedChauffeurDocs?.nom}
            </DialogTitle>
            <DialogDescription>
              Visualiser et télécharger les documents du chauffeur
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-4 h-[70vh]">
            {/* Left side - Document list */}
            <div className="col-span-1 overflow-y-auto border-r pr-4">
              {loadingDocuments ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Chargement...</p>
                </div>
              ) : chauffeurDocuments.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">Aucun document</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {chauffeurDocuments.map((doc, index) => (
                    <button
                      key={index}
                      onClick={() => setPreviewDocument(doc)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        previewDocument?.filename === doc.filename
                          ? 'bg-emerald-50 border-emerald-500'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <File className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-sm">
                          {doc.type === 'permis_file' ? '📄 PDF' :
                           doc.type === 'permis_photo' ? '📸 Recto' :
                           doc.type === 'permis_photo_verso' ? '📸 Verso' :
                           'Document'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 truncate">
                        {doc.filename || doc.file_path?.split('/').pop()}
                      </p>
                      {doc.date_upload && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(doc.date_upload).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Right side - Preview */}
            <div className="col-span-2 overflow-hidden flex flex-col">
              {previewDocument ? (
                <>
                  <div className="flex items-center justify-between mb-3 pb-3 border-b">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {previewDocument.type === 'permis_file' ? '📄 Permis de conduire (PDF)' :
                         previewDocument.type === 'permis_photo' ? '📸 Photo permis (recto)' :
                         previewDocument.type === 'permis_photo_verso' ? '📸 Photo permis (verso)' :
                         'Document'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {previewDocument.filename || previewDocument.file_path?.split('/').pop()}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadDocument(previewDocument)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                  
                  <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden">
                    {/* Preview based on file type */}
                    {previewDocument.filename?.toLowerCase().endsWith('.pdf') || previewDocument.type === 'permis_file' ? (
                      <iframe
                        src={previewDocument.previewUrl}
                        className="w-full h-full"
                        title="Prévisualisation PDF"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <img
                          src={previewDocument.previewUrl}
                          alt="Prévisualisation"
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.parentElement) {
                              e.target.parentElement.innerHTML = '<p class="text-gray-600">Impossible de charger l\'image</p>';
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
                  <p className="text-gray-600">
                    {chauffeurDocuments.length > 0 
                      ? 'Sélectionnez un document à gauche pour le prévisualiser'
                      : 'Aucun document à prévisualiser'}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentsDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Upload Permis */}
      <Dialog open={uploadPermisDialogOpen} onOpenChange={setUploadPermisDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Permis de Conduire</DialogTitle>
            <DialogDescription>
              {selectedChauffeurForUpload && `Ajouter un permis pour ${selectedChauffeurForUpload.nom}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="upload-permis-numero">Numéro de permis *</Label>
              <Input
                id="upload-permis-numero"
                value={permisUploadData.numero}
                onChange={(e) => setPermisUploadData({ ...permisUploadData, numero: e.target.value })}
                placeholder="Ex: ABC123456"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-permis-emission">Date d'émission *</Label>
              <Input
                id="upload-permis-emission"
                type="date"
                value={permisUploadData.date_emission}
                onChange={(e) => setPermisUploadData({ ...permisUploadData, date_emission: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-permis-expiration">Date d'expiration *</Label>
              <Input
                id="upload-permis-expiration"
                type="date"
                value={permisUploadData.date_expiration}
                onChange={(e) => setPermisUploadData({ ...permisUploadData, date_expiration: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-permis-file">Fichier du permis (PDF, JPG, PNG) *</Label>
              <Input
                id="upload-permis-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setPermisUploadData({ ...permisUploadData, file: e.target.files[0] })}
                required
              />
              {permisUploadData.file && (
                <p className="text-sm text-gray-600">Fichier sélectionné: {permisUploadData.file.name}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadPermisDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handlePermisUpload}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Chauffeurs;
